import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import ImageUpload from '../../components/admin/ImageUpload';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL, getUploadUrl } from '../../config/api';
import './css/AdminPages.css';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    price: '',
    calories: '',
    image: '',
    ingredients_ar: [],
    ingredients_en: [],
    display_order: 0,
    is_active: true
  });
  const [ingredientAr, setIngredientAr] = useState('');
  const [ingredientEn, setIngredientEn] = useState('');
  const [sizes, setSizes] = useState([]);
  const [showSizesModal, setShowSizesModal] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [sizeFormData, setSizeFormData] = useState({
    size_name_ar: '',
    size_name_en: '',
    price: '',
    display_order: 0,
    is_active: true
  });
  const [editingSize, setEditingSize] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, isRTL, toggleLanguage } = useLanguage();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      if (editingProduct) {
        await axios.put(
          `${API_URL}/api/products/${editingProduct.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/products`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      fetchProducts();
      handleCloseModal();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error saving product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      category_id: product.category_id,
      name_ar: product.name_ar,
      name_en: product.name_en,
      description_ar: product.description_ar || '',
      description_en: product.description_en || '',
      price: product.price,
      calories: product.calories || '',
      image: product.image || '',
      ingredients_ar: Array.isArray(product.ingredients_ar) ? product.ingredients_ar : [],
      ingredients_en: Array.isArray(product.ingredients_en) ? product.ingredients_en : [],
      display_order: product.display_order,
      is_active: product.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/products/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error deleting product');
    }
  };

  // Sizes Management
  const openSizesModal = async (productId) => {
    setCurrentProductId(productId);
    setShowSizesModal(true);
    await fetchSizes(productId);
  };

  const fetchSizes = async (productId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/product-sizes/product/${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSizes(response.data);
    } catch (error) {
      console.error('Error fetching sizes:', error);
      setSizes([]);
    }
  };

  const handleSizeSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      if (editingSize) {
        await axios.put(
          `${API_URL}/api/product-sizes/${editingSize.id}`,
          { ...sizeFormData, product_id: currentProductId },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/product-sizes`,
          { ...sizeFormData, product_id: currentProductId },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      await fetchSizes(currentProductId);
      setSizeFormData({
        size_name_ar: '',
        size_name_en: '',
        price: '',
        display_order: 0,
        is_active: true
      });
      setEditingSize(null);
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error saving size');
    }
  };

  const handleEditSize = (size) => {
    setEditingSize(size);
    setSizeFormData({
      size_name_ar: size.size_name_ar,
      size_name_en: size.size_name_en,
      price: size.price,
      display_order: size.display_order,
      is_active: size.is_active
    });
  };

  const handleDeleteSize = async (sizeId) => {
    if (!window.confirm(t('admin.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/product-sizes/${sizeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchSizes(currentProductId);
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error deleting size');
    }
  };

  const closeSizesModal = () => {
    setShowSizesModal(false);
    setCurrentProductId(null);
    setSizes([]);
    setEditingSize(null);
    setSizeFormData({
      size_name_ar: '',
      size_name_en: '',
      price: '',
      display_order: 0,
      is_active: true
    });
  };

  const handleAddIngredient = () => {
    if (ingredientAr.trim() || ingredientEn.trim()) {
      setFormData({
        ...formData,
        ingredients_ar: [...formData.ingredients_ar, ingredientAr.trim()],
        ingredients_en: [...formData.ingredients_en, ingredientEn.trim()]
      });
      setIngredientAr('');
      setIngredientEn('');
    }
  };

  const handleRemoveIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients_ar: formData.ingredients_ar.filter((_, i) => i !== index),
      ingredients_en: formData.ingredients_en.filter((_, i) => i !== index)
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData({
      category_id: '',
      name_ar: '',
      name_en: '',
      description_ar: '',
      description_en: '',
      price: '',
      calories: '',
      image: '',
      ingredients_ar: [],
      ingredients_en: [],
      display_order: 0,
      is_active: true
    });
    setIngredientAr('');
    setIngredientEn('');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>{t('admin.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="admin-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <ArrowLeftIcon className="icon" />
          {t('admin.back')}
        </button>
        <h1>{t('admin.products')}</h1>
        <div className="header-actions">
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'ar' ? 'EN' : 'AR'}
          </button>
          <button onClick={() => setShowModal(true)} className="add-btn">
            + {t('admin.add')} {t('admin.products')}
          </button>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.id')}</th>
              <th>{t('admin.nameAr')}</th>
              <th>{t('admin.nameEn')}</th>
              <th>{t('admin.category')}</th>
              <th>{t('admin.price')}</th>
              <th>{t('admin.calories')}</th>
              <th>{t('admin.image')}</th>
              <th>{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td>{product.id}</td>
                <td>{product.name_ar}</td>
                <td>{product.name_en}</td>
                <td>{product.category_key}</td>
                <td>{product.price} {t('products.currency')}</td>
                <td>{product.calories || '-'}</td>
                <td>
                  {product.image && (
                    <img src={getUploadUrl(product.image)} alt={product.name_ar} className="table-image" />
                  )}
                </td>
                <td>
                  <button onClick={() => handleEdit(product)} className="edit-btn">{t('admin.edit')}</button>
                  <button onClick={() => openSizesModal(product.id)} className="edit-btn" style={{ marginLeft: '5px', backgroundColor: '#17a2b8' }}>
                    {t('admin.manageSizes') || 'Sizes'}
                  </button>
                  <button onClick={() => handleDelete(product.id)} className="delete-btn">{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingProduct ? t('admin.editProduct') : t('admin.addProduct')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('admin.category')} *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  required
                  className="form-select"
                >
                  <option value="">{t('admin.selectCategory')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name_en} ({cat.key_name})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('admin.nameAr')} *</label>
                <input
                  type="text"
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.nameEn')} *</label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.descriptionAr')}</label>
                <textarea
                  value={formData.description_ar}
                  onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.descriptionEn')}</label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.priceSAR')} *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.calories')}</label>
                <input
                  type="number"
                  min="0"
                  value={formData.calories}
                  onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
                  placeholder={t('admin.caloriesPlaceholder')}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.image')}</label>
                <ImageUpload
                  onUploadComplete={(filename) => setFormData({ ...formData, image: filename })}
                  currentImage={formData.image ? `/uploads/${formData.image}` : null}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.ingredients')}</label>
                <div className="ingredient-inputs">
                  <input
                    type="text"
                    placeholder={t('admin.ingredientAr')}
                    value={ingredientAr}
                    onChange={(e) => setIngredientAr(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder={t('admin.ingredientEn')}
                    value={ingredientEn}
                    onChange={(e) => setIngredientEn(e.target.value)}
                  />
                  <button type="button" onClick={handleAddIngredient} className="add-btn-small">{t('admin.addIngredient')}</button>
                </div>
                <div className="ingredients-list">
                  {formData.ingredients_ar.map((ing, index) => (
                    <div key={index} className="ingredient-item">
                      <span>{ing} / {formData.ingredients_en[index]}</span>
                      <button type="button" onClick={() => handleRemoveIngredient(index)} className="delete-btn-small">{t('admin.remove')}</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>{t('admin.displayOrder')}</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {t('admin.active')}
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} className="cancel-btn">{t('admin.cancel')}</button>
                <button type="submit" className="save-btn">{t('admin.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sizes Management Modal */}
      {showSizesModal && (
        <div className="modal-overlay" onClick={closeSizesModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>{t('admin.manageSizes') || 'Manage Product Sizes'}</h2>
            <form onSubmit={handleSizeSubmit}>
              <div className="form-group">
                <label>{t('admin.sizeNameAr') || 'Size Name (Arabic)'} *</label>
                <input
                  type="text"
                  value={sizeFormData.size_name_ar}
                  onChange={(e) => setSizeFormData({ ...sizeFormData, size_name_ar: e.target.value })}
                  required
                  placeholder="صغير"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.sizeNameEn') || 'Size Name (English)'} *</label>
                <input
                  type="text"
                  value={sizeFormData.size_name_en}
                  onChange={(e) => setSizeFormData({ ...sizeFormData, size_name_en: e.target.value })}
                  required
                  placeholder="Small"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.priceSAR')} *</label>
                <input
                  type="number"
                  step="0.01"
                  value={sizeFormData.price}
                  onChange={(e) => setSizeFormData({ ...sizeFormData, price: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.displayOrder')}</label>
                <input
                  type="number"
                  value={sizeFormData.display_order}
                  onChange={(e) => setSizeFormData({ ...sizeFormData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={sizeFormData.is_active}
                    onChange={(e) => setSizeFormData({ ...sizeFormData, is_active: e.target.checked })}
                  />
                  {t('admin.active')}
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setEditingSize(null); setSizeFormData({ size_name_ar: '', size_name_en: '', price: '', display_order: 0, is_active: true }); }} className="cancel-btn">
                  {editingSize ? t('admin.cancel') : t('admin.clear') || 'Clear'}
                </button>
                <button type="submit" className="save-btn">{editingSize ? t('admin.update') || 'Update' : t('admin.add')}</button>
              </div>
            </form>

            {/* Sizes List */}
            <div style={{ marginTop: '30px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
              <h3>{t('admin.sizesList') || 'Sizes List'}</h3>
              {sizes.length === 0 ? (
                <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                  {t('admin.noSizes') || 'No sizes added yet'}
                </p>
              ) : (
                <table className="admin-table" style={{ marginTop: '15px' }}>
                  <thead>
                    <tr>
                      <th>{t('admin.sizeNameAr')}</th>
                      <th>{t('admin.sizeNameEn')}</th>
                      <th>{t('admin.price')}</th>
                      <th>{t('admin.order')}</th>
                      <th>{t('admin.active')}</th>
                      <th>{t('admin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.map((size) => (
                      <tr key={size.id}>
                        <td>{size.size_name_ar}</td>
                        <td>{size.size_name_en}</td>
                        <td>{size.price} {t('products.currency') || 'SAR'}</td>
                        <td>{size.display_order}</td>
                        <td>{size.is_active ? t('admin.yes') : t('admin.no')}</td>
                        <td>
                          <button onClick={() => handleEditSize(size)} className="edit-btn">{t('admin.edit')}</button>
                          <button onClick={() => handleDeleteSize(size.id)} className="delete-btn">{t('admin.delete')}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
              <button type="button" onClick={closeSizesModal} className="cancel-btn">{t('admin.close') || 'Close'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
